/**
 * Count Up — animate numbers from 0 to a target value when they scroll into view.
 *
 * Markup contract:
 *   <span data-count-up="20">20</span>          — int target
 *   <span data-count-up="0.4" data-decimals="1">0.4</span>
 *   <span data-count-up="16.4" data-decimals="1" data-suffix=" ft">16.4 ft</span>
 *   <span data-count-up="1.57" data-decimals="2">1.57</span>
 *   <span data-count-up-duration="1400">…</span>  — override default 1100ms
 *
 * The element's textContent is replaced with the live value during animation.
 * Honours prefers-reduced-motion (skips animation and writes final value once).
 */
(function () {
  'use strict';

  if (typeof window === 'undefined') return;

  var DEFAULT_DURATION = 1100;
  var prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // easeOutCubic — slows to a stop, feels confident, never overshoots.
  function easeOutCubic(t) {
    var u = 1 - t;
    return 1 - u * u * u;
  }

  function format(value, decimals) {
    if (decimals > 0) return value.toFixed(decimals);
    return Math.round(value).toString();
  }

  function animate(el) {
    var target = parseFloat(el.dataset.countUp);
    if (isNaN(target)) return;

    var decimals = parseInt(el.dataset.decimals || '0', 10);
    var duration = parseInt(el.dataset.countUpDuration || DEFAULT_DURATION, 10);
    var prefix = el.dataset.prefix || '';
    var suffix = el.dataset.suffix || '';

    if (prefersReduced || duration <= 0) {
      el.textContent = prefix + format(target, decimals) + suffix;
      return;
    }

    var start = null;
    function tick(timestamp) {
      if (start === null) start = timestamp;
      var elapsed = timestamp - start;
      var progress = Math.min(elapsed / duration, 1);
      var current = target * easeOutCubic(progress);
      el.textContent = prefix + format(current, decimals) + suffix;
      if (progress < 1) {
        requestAnimationFrame(tick);
      } else {
        el.textContent = prefix + format(target, decimals) + suffix;
      }
    }
    requestAnimationFrame(tick);
  }

  if (!('IntersectionObserver' in window)) {
    // Old browser fallback: set final values immediately.
    document.querySelectorAll('[data-count-up]').forEach(function (el) {
      var target = parseFloat(el.dataset.countUp);
      var decimals = parseInt(el.dataset.decimals || '0', 10);
      var prefix = el.dataset.prefix || '';
      var suffix = el.dataset.suffix || '';
      if (!isNaN(target)) el.textContent = prefix + format(target, decimals) + suffix;
    });
    return;
  }

  var observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      var el = entry.target;
      if (el.dataset.countUpStarted === 'true') return;
      el.dataset.countUpStarted = 'true';
      animate(el);
      observer.unobserve(el);
    });
  }, {
    rootMargin: '0px 0px -8% 0px',
    threshold: 0.3
  });

  function init(root) {
    var scope = root || document;
    var targets = scope.querySelectorAll('[data-count-up]:not([data-count-up-started])');
    targets.forEach(function (el) {
      // Seed displayed value at zero so users don't see the final number flash before reveal.
      var decimals = parseInt(el.dataset.decimals || '0', 10);
      var prefix = el.dataset.prefix || '';
      var suffix = el.dataset.suffix || '';
      el.textContent = prefix + format(0, decimals) + suffix;
      observer.observe(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  window.MemoketCountUp = { observe: init };
})();
